import './UserCurrent.scss';
import React, { FC, useContext, useEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import type { GetUserCurrentQuery } from '@/generated/graphql';
import GetUserCurrent from './graphql/UserCurrent.gql';
import {
    removeOAuth2Store,
    setUserCurrentStore,
    getUserCurrentStore,
    removeUserCurrentStore,
    removeFoyerServiceStore,
} from '@/store';
import { getUsersCurrentEditPermission } from '@/components/user/UserCurrent/userCurrentPermission';
import { contentEditUserRoles } from '@/components/user/UserCurrent/userCurrentPermission';
import { AuthenticationContext } from '@/components/provider/AuthenticationProvider';
import { UserCurrentContext } from '@/components/provider/UserCurrentProvider';
import {
    useFoyerConfig,
    useFoyerConfigDispatch,
} from '@/components/edifice/Foyer/FoyerProvider';
import ButtonIcon from '@/components/basic/ButtonIcon/ButtonIcon';
import Switch from '@/components/basic/Switch/Switch';
import classNames from 'classnames';

export const editableComponents = [
    'REALM',
    'ZONE',
    'MATRIX',
    'CLUSTER',
    'PANEL',
    'MEDIA',
] as const;

export const editableComponentNames = editableComponents.reduce(
    (acc, curr) => {
        acc[curr] = curr.charAt(0) + curr.slice(1).toLowerCase();
        return acc;
    },
    {} as Record<(typeof editableComponents)[number], string>,
);

type UserCurrentProps = Record<string, never>;

const UserCurrent: FC<UserCurrentProps> = () => {
    const [accessToken, setAccessToken] = useContext(AuthenticationContext);

    const context = useContext(UserCurrentContext);
    if (!context)
        throw new Error('UserCurrent must be used within UserCurrentProvider');
    const { userCurrent, setUserCurrent } = context;

    const foyerConfig = useFoyerConfig();
    const foyerConfigDispatch = useFoyerConfigDispatch();
    const client = useApolloClient();

    const { loading, error, data } = useQuery<GetUserCurrentQuery>(
        GetUserCurrent,
        {
            skip: !accessToken,
            fetchPolicy: 'network-only',
        },
    );

    useEffect(() => {
        if (!data?.userCurrent || loading) return;

        setUserCurrent((prevState) => {
            const isContentEditor = contentEditUserRoles.some((role) =>
                data.userCurrent.roles.includes(role),
            );
            const stored = getUserCurrentStore();

            if (prevState?.id === data.userCurrent.id) {
                return {
                    ...prevState,
                    ...data.userCurrent,
                };
            }

            return {
                ...data.userCurrent,
                isContentEditor,
                isEditMode:
                    stored?.id === data.userCurrent.id
                        ? stored.isEditMode
                        : undefined,
            };
        });
    }, [data, loading, setUserCurrent]);

    const logOut = async () => {
        removeOAuth2Store();
        removeUserCurrentStore();
        removeFoyerServiceStore();

        setAccessToken(null);
        setUserCurrent(null);

        await client.clearStore();
        window.location.href = '/';
    };

    const setEditMode = (component) => {
        setUserCurrent((prev) => {
            if (!prev) return null;
            const isEditMode =
                component === prev.isEditMode ? undefined : component;
            setUserCurrentStore({ id: prev.id, isEditMode });
            return { ...prev, isEditMode };
        });
    };

    if (!accessToken) {
        return null;
    }

    const hasData = data?.userCurrent && !error;
    const isActiveUserServiceMenu = foyerConfig.activeServiceMenu === 'user';

    const showUserCurrentMenu = () => {
        foyerConfigDispatch({
            type: 'SET_ACTIVE_SERVICE_MENU',
            serviceMenu: 'user',
            serviceMenuTitle: userCurrent?.name,
        });
    };

    return (
        <>
            <section className="Service_item user">
                <div className="Service_link" onClick={showUserCurrentMenu}>
                    <ButtonIcon
                        className="Service_icon"
                        icon="user"
                        label={userCurrent?.name || 'Loading...'}
                    />
                    <h2 className="Service_title">
                        {userCurrent?.name || '...'}
                    </h2>
                </div>
                <ButtonIcon
                    icon="chevron"
                    label="Anwendungsmenü öffnen"
                    className="Service_next"
                    onClick={showUserCurrentMenu}
                />
            </section>
            <section
                className={classNames('Service_menu', {
                    isOpen: isActiveUserServiceMenu,
                })}
            >
                {hasData ? (
                    <ul className="UserCurrent_actions">
                        <li className="UserCurrent_action" onClick={logOut}>
                            <span className="UserCurrent_label">
                                <ButtonIcon icon="logout" />
                                <span>Logout</span>
                            </span>
                        </li>
                        {userCurrent?.isContentEditor && (
                            <li className="UserCurrent_action">
                                <Switch
                                    icon="enableEditing"
                                    checked={!!userCurrent?.isEditMode}
                                    onChange={() =>
                                        setEditMode(!userCurrent?.isEditMode)
                                    }
                                >
                                    Enable edit mode
                                </Switch>

                                {userCurrent.isEditMode && (
                                    <ul>
                                        {editableComponents
                                            .filter(
                                                (componentType) =>
                                                    Object.values(
                                                        getUsersCurrentEditPermission(
                                                            {
                                                                componentType:
                                                                    componentType,
                                                                currentUserId:
                                                                    userCurrent.id,
                                                                currentUserRoles:
                                                                    userCurrent.roles,
                                                            },
                                                        ),
                                                    ).length,
                                            )
                                            .map((componentType) => (
                                                <li
                                                    key={componentType}
                                                    className="UserCurrent_subAction"
                                                >
                                                    <Switch
                                                        labelFixedGap={true}
                                                        checked={
                                                            userCurrent?.isEditMode ===
                                                            componentType
                                                        }
                                                        onChange={() =>
                                                            setEditMode(
                                                                componentType,
                                                            )
                                                        }
                                                    >{`${componentType.substring(0, 1)}${componentType
                                                        .substring(1)
                                                        .toLowerCase()}`}</Switch>
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </li>
                        )}
                    </ul>
                ) : (
                    <p className="UserCurrent_loading">
                        {error ? 'Fehler beim Laden' : 'Lade Benutzerdaten...'}
                    </p>
                )}
            </section>
        </>
    );
};

export default UserCurrent;
